import {AfterViewInit, Component, Input} from '@angular/core';
import * as $ from "jquery";
import * as d3 from "d3";
import moment from "moment";
import {CommonService} from '../../../../shared/services/common.service';
import {DatePipe, DecimalPipe} from '@angular/common';

@Component({
  selector: 'app-physical-activity-chart',
  templateUrl: './physical-activity-chart.component.html',
  styleUrls: ['./physical-activity-chart.component.css']
})
export class PhysicalActivityChartComponent implements AfterViewInit {
  @Input() data;

  colorMap = {
    paPoints: '#F9D949',
    targetNotMet: '#59B3A9',
    target: '#59B3A9'
  }

  legend = [
    {name: 'Red Line', color: '#F9D949'},
    {name: 'Target', color: '#3C486B'},

  ]

  svg: any;
  margins = {
    top: 10,
    right: 0,
    bottom: 50,
    left: 20,
  };
  width = 100;
  height = 100;

  isWarned = false;

  resizeObserver: ResizeObserver;

  constructor(private commonService: CommonService, private datePipe: DatePipe, private decimalPipe: DecimalPipe) {
    // creating resize observer for detecting size change of chart container
    this.resizeObserver = new ResizeObserver(() => {
      this.ngAfterViewInit();
    })
  }


  ngOnChanges() {
    this.ngAfterViewInit();
  }

  ngOnInit() {
    setTimeout(() => {
      const chartContainer = document.getElementById('physical-activity-chart');
      this.resizeObserver.observe(chartContainer);
    });
  }

  ngAfterViewInit() {
    if (this.data) {
      const container = $('#physical-activity-chart');
      this.width = container.width();
      this.height = container.height();
      this.createChart();
    }
  }

  createChart() {
    // Kept this warning for the QA and any other user who might face this
    // issue.
    if (!this.isWarned) {
      console.warn(`The chart disappears when the screen is in responsive and the screen size selection is changed from 'Responsive' to any other mobile screen and vice-versa. This is a known issue and needs to be explored. However, this issue does not impact the application or the user experience.`);
    }

    this.isWarned = true;

    d3.select("#physical-activity-chart svg").remove();
    this.svg = d3.select("#physical-activity-chart")
      .append("svg")
      .attr("width", '100%')
      .attr("height", '100%')
      .attr('viewBox', `0, -10, ${this.width}, ${this.height + this.margins.bottom - 15}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append("g")

    // Create the X-axis band scale
    const x = d3.scaleBand()
      .range([0, this.width])
      .domain(this.data?.map(d => this.datePipe.transform(d.date, 'MMMdd')))
      .padding(0.5)

    // Create the Y-axis band scale

    const yMax = d3.max(this.data, (d) => Math.max(d['paPoints'], d['paGoal'])) || 0
    const y = d3.scaleLinear()
      .domain([0, yMax + yMax * 0.2])
      .rangeRound([this.height, 0]);

    const yAxis = d3.axisLeft(y);
    this.svg.append('g')
      .call((() => {
          if (yMax === 0) {
            return yAxis.tickValues([])
          } else {
            return yAxis
              .ticks(4)
              .tickFormat((d) => d3.format(',')(Math.floor(Number(d))))
          }
        })()
      )

    // ********** Create tooltip ********** //
    const tooltip = d3.select("#physical-activity-chart")
      .append("div")
      .attr('class', 'tooltip')
      .style("position", "fixed")
      .style("visibility", 'hidden')

    // Create and fill the bars
    const selector = this.svg.selectAll(".bar")
      .data(this.data)
      .enter()

    selector.append("rect")
      .attr("x", (d: any) => x(this.datePipe.transform(d.date, 'MMMdd')))
      .attr("y", (d: any) => y(d.paPoints))
      .attr("width", x.bandwidth())
      .attr("height", (d: any) => d.paPoints ? (this.height - y(d.paPoints)) : 0)
      .attr("fill", (d: any) => d.notMeetingTarget ? this.colorMap.paPoints : this.colorMap.targetNotMet)
      .on("mouseover", (event: any, d) => {
        return tooltip
          .html(`
            ${d.imputed ? `Estimated<br>` : ``}
            ${moment(d.date).format('MMM DD, YYYY')}<br>
            ${this.decimalPipe.transform(d.paPoints)} Activity Points
          `)
          .transition()
          .duration(200)
          .style("top", (event.clientY - this.commonService.convertRemToPx(7)) + "px")
          .style("left", this.commonService.calculateXForPopup(tooltip, event.clientX) + "px")
          .style("visibility", 'visible');
      })
      .on("mousemove", (event: any) => {
        return tooltip
          .transition()
          .duration(0)
          .style("top", (event.clientY - this.commonService.convertRemToPx(7)) + "px")
          .style("left", this.commonService.calculateXForPopup(tooltip, event.clientX) + "px");
      })
      .on("mouseout", (event: any) => {
        return tooltip
          .transition()
          .duration(0)
          .style("visibility", 'hidden');
      });

// ********** COMBINED TARGET LINE ********** //
    var targetGoalArr = this.data.filter(o => o.paGoal).map(o => o.paGoal);

    var target = this.svg.selectAll(".paGoal")
      .data(targetGoalArr)
      .enter()
      .append("g")
      .attr("transform", function (d) {
        return "translate(0, " + y(d) + ")"
      })

    if (targetGoalArr.length) {
      target.append("line")
        .attr("class", "paGoal")
        .attr("x1", 0)
        .attr("x2", this.width)
        .style("stroke-width", "1")
        .style("stroke", "#0070E0");
    }


    // Draw the X-axis on the DOM
    this.svg.append("g")
      .attr("transform", "translate(0," + this.height + ")")
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("text-anchor", "center")
      .style("font-size", "1.2rem")
      .call(function (t) {
        t.each(function () { // for each one
          const self = d3.select(this);
          const s = self.text().split(' ');  // get the text and split it
          self.text(''); // clear it out
          s.forEach((d, i) => {
            self.append("tspan")
              .attr("x", 0)
              .attr("dy", "1em")
              .text(s[i]);
          })
        })
      })
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }
}
